import React, { useState, useEffect } from 'react';
import api from '../api';
import {
  Sliders, Shield, Layout, Eye, Lock, EyeOff, CheckCircle,
  Save, RefreshCw, Search, AlertCircle, Info, ChevronRight
} from 'lucide-react';
import './RoleConfigurationPage.css';

function RoleConfigurationPage({ onConfigurationSaved }) {
  const [roles, setRoles] = useState([]);
  const [selectedTargetId, setSelectedTargetId] = useState('');
  
  const [manifestPages, setManifestPages] = useState([]);
  const [selectedPageKey, setSelectedPageKey] = useState('');
  
  const [rules, setRules] = useState({}); // { "page_key:element_key": "FULL" | "READONLY" | "HIDDEN" }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const [elementSearchTerm, setElementSearchTerm] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedTargetId) {
      fetchRulesForTarget('role', selectedTargetId);
    } else {
      setRules({});
    }
  }, [selectedTargetId]);



  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [manifestRes, rolesRes] = await Promise.all([
        api.get('/university/ui-config/manifest'),
        api.get('/university/roles')
      ]);

      const pages = manifestRes.data?.pages || [];
      setManifestPages(pages);
      if (pages.length > 0) {
        setSelectedPageKey(pages[0].page_key);
      }

      const fetchedRoles = rolesRes.data || [];
      setRoles(fetchedRoles);

      if (fetchedRoles.length > 0) {
        setSelectedTargetId(fetchedRoles[0].id);
      }
    } catch (err) {
      console.error("Failed to load UI configuration data", err);
      showToast("Error loading page manifest or roles.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchRulesForTarget = async (targetType, targetId) => {
    try {
      const res = await api.get(`/university/ui-config/rules?target_type=${targetType}&target_id=${targetId}`);
      const fetchedRulesList = res.data?.rules || [];
      
      const newRulesMap = {};
      fetchedRulesList.forEach(r => {
        newRulesMap[`${r.page_key}:${r.element_key}`] = r.access_level;
      });

      setRules(newRulesMap);
    } catch (err) {
      console.error("Failed to fetch rules for target", err);
      showToast("Failed to fetch current rules for selected target.", "error");
    }
  };

  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 4000);
  };

  const handleAccessChange = (pageKey, elementKey, accessLevel) => {
    setRules(prev => ({
      ...prev,
      [`${pageKey}:${elementKey}`]: accessLevel
    }));
  };

  const handleBulkPageAccess = (pageKey, accessLevel) => {
    const currentPageObj = manifestPages.find(p => p.page_key === pageKey);
    if (!currentPageObj || !currentPageObj.elements) return;

    setRules(prev => {
      const updated = { ...prev };
      currentPageObj.elements.forEach(el => {
        updated[`${pageKey}:${el.element_key}`] = accessLevel;
      });
      return updated;
    });
  };

  const handleSaveConfiguration = async () => {
    if (!selectedTargetId) {
      showToast("Please select a Role first.", "error");
      return;
    }

    try {
      setSaving(true);
      const rulesList = [];

      manifestPages.forEach(p => {
        if (!p.elements) return;
        p.elements.forEach(el => {
          const key = `${p.page_key}:${el.element_key}`;
          const level = rules[key] || 'FULL';
          if (level !== 'FULL') {
            rulesList.push({
              page_key: p.page_key,
              element_key: el.element_key,
              access_level: level
            });
          }
        });
      });

      await api.post('/university/ui-config/rules', {
        target_type: 'role',
        target_id: parseInt(selectedTargetId),
        rules: rulesList
      });

      showToast(`UI Access Policy saved for Role successfully!`, "success");
      if (typeof onConfigurationSaved === 'function') {
        onConfigurationSaved();
      }
    } catch (err) {
      console.error("Error saving UI rules", err);
      showToast("Failed to save UI configuration.", "error");
    } finally {
      setSaving(false);
    }
  };

  const selectedPage = manifestPages.find(p => p.page_key === selectedPageKey);
  const filteredElements = selectedPage?.elements?.filter(el => {
    const term = elementSearchTerm.toLowerCase();
    return (el.title || '').toLowerCase().includes(term) ||
           (el.element_key || '').toLowerCase().includes(term) ||
           (el.type || '').toLowerCase().includes(term);
  }) || [];



  if (loading) {
    return (
      <div className="ui-config-loading">
        <RefreshCw size={24} className="spin-icon" />
        <p>Loading UI Role & Page Configuration Engine...</p>
      </div>
    );
  }

  return (
    <div className="ui-config-container">
      {toastMessage && (
        <div className={`ui-config-toast toast-${toastMessage.type}`}>
          {toastMessage.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
          <span>{toastMessage.message}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="ui-config-header">
        <div className="header-title-box">
          <div className="header-icon">
            <Sliders size={26} />
          </div>
          <div>
            <h2>Page & Element UI Role Configuration</h2>
            <p className="subtitle">
              Configure granular access (Full Access, Read Only, or Denied/Hidden) for every page, tab, button, text area, and input field across the portal.
            </p>
          </div>
        </div>

        <button
          className="save-config-btn"
          onClick={handleSaveConfiguration}
          disabled={saving || !selectedTargetId}
        >
          {saving ? <RefreshCw size={18} className="spin-icon" /> : <Save size={18} />}
          {saving ? 'Saving Configuration...' : 'Save Configuration'}
        </button>
      </div>

      {/* Target Selector Card (Role Configuration) */}
      <div className="ui-config-card target-selector-card">
        <div className="target-selection-row">
          <div className="select-group">
            <label><Shield size={16} /> Select Target Role:</label>
            <select
              className="target-dropdown"
              value={selectedTargetId}
              onChange={(e) => setSelectedTargetId(e.target.value)}
            >
              {roles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name || r.role_name}{r.assigned_emails && r.assigned_emails.length > 0 ? ` - ${r.assigned_emails[0]}` : ''} ({r.permission_count || (r.permissions ? r.permissions.length : 0)} System Permissions)
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Layout: Left Page Navigation Explorer + Right Element Matrix */}
      <div className="ui-config-main-layout">
        {/* Left Sidebar: Page List Explorer */}
        <div className="ui-config-card page-explorer-sidebar">
          <div className="sidebar-header">
            <Layout size={18} />
            <h3>Pages Explorer</h3>
          </div>
          <div className="page-list-menu">
            {manifestPages.map(page => {
              const isSelected = page.page_key === selectedPageKey;
              return (
                <div
                  key={page.page_key}
                  className={`page-menu-item ${isSelected ? 'active' : ''}`}
                  onClick={() => setSelectedPageKey(page.page_key)}
                >
                  <div className="page-title-row">
                    <span>{page.page_title}</span>
                    <ChevronRight size={16} className="arrow-icon" />
                  </div>
                  <span className="element-count-badge">
                    {page.elements.length} configurable elements
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Content Area: Granular Elements Matrix Grid */}
        <div className="ui-config-card element-matrix-area">
          {selectedPage ? (
            <>
              {/* Matrix Top Action Bar */}
              <div className="matrix-top-bar">
                <div>
                  <h3>{selectedPage.page_title} Elements Access Matrix</h3>
                  <p className="page-subtitle">Configure individual tabs, action buttons, text areas, and input fields on this page.</p>
                </div>

                <div className="bulk-actions-wrapper">
                  <span className="bulk-label">Bulk Apply Page:</span>
                  <button className="bulk-btn btn-full" onClick={() => handleBulkPageAccess(selectedPage.page_key, 'FULL')}>
                    <Eye size={14} /> Full Access
                  </button>
                  <button className="bulk-btn btn-readonly" onClick={() => handleBulkPageAccess(selectedPage.page_key, 'READONLY')}>
                    <Lock size={14} /> Read Only
                  </button>
                  <button className="bulk-btn btn-hidden" onClick={() => handleBulkPageAccess(selectedPage.page_key, 'HIDDEN')}>
                    <EyeOff size={14} /> Denied / Hidden
                  </button>
                </div>
              </div>

              {/* Element Filter Search Bar */}
              <div className="matrix-search-box">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search element title, key or type (e.g. Button, Input, Tab)..."
                  value={elementSearchTerm}
                  onChange={(e) => setElementSearchTerm(e.target.value)}
                />
              </div>

              {/* Elements List */}
              <div className="elements-matrix-list">
                {filteredElements.length === 0 ? (
                  <div className="no-elements-found">
                    <Info size={24} />
                    <p>No UI elements found matching your search filter.</p>
                  </div>
                ) : (
                  filteredElements.map(el => {
                    const ruleKey = `${selectedPage.page_key}:${el.element_key}`;
                    const currentLevel = rules[ruleKey] || 'FULL';

                    return (
                      <div key={el.element_key} className={`matrix-row-card access-mode-${currentLevel.toLowerCase()}`}>
                        <div className="element-meta">
                          <span className={`element-type-tag type-${el.type.toLowerCase()}`}>
                            {el.type}
                          </span>
                          <div className="element-title-info">
                            <h4>{el.title}</h4>
                          </div>
                        </div>

                        {/* Access Mode Selector Buttons */}
                        <div className="access-level-options">
                          <button
                            type="button"
                            className={`access-option option-full ${currentLevel === 'FULL' ? 'selected' : ''}`}
                            onClick={() => handleAccessChange(selectedPage.page_key, el.element_key, 'FULL')}
                          >
                            <Eye size={16} />
                            <span>Full Access</span>
                          </button>

                          <button
                            type="button"
                            className={`access-option option-readonly ${currentLevel === 'READONLY' ? 'selected' : ''}`}
                            onClick={() => handleAccessChange(selectedPage.page_key, el.element_key, 'READONLY')}
                          >
                            <Lock size={16} />
                            <span>Read Only</span>
                          </button>

                          <button
                            type="button"
                            className={`access-option option-hidden ${currentLevel === 'HIDDEN' ? 'selected' : ''}`}
                            onClick={() => handleAccessChange(selectedPage.page_key, el.element_key, 'HIDDEN')}
                          >
                            <EyeOff size={16} />
                            <span>Denied (Hidden)</span>
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="select-page-placeholder">
              <Layout size={32} />
              <p>Select a page from the left explorer to configure its UI elements.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoleConfigurationPage;
